import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    createObjectifRequest,
    fetchProgressObjectifModificationRequestsRequest,
    fetchObjectifsNRequest,
    submitObjectifModificationRequest,
} from "../../../../redux/actions/performanceActions";
import { asArray, StepState } from "../performanceUi";
import ObjectifsAIAssistanceModal, { buildObjectifsAiStats } from "./ObjectifsAIAssistanceModal";
import "./ObjectifsNStep.css";

const emptyObjectif = {
    titre: "",
    description: "",
    indicateur: "",
    performanceCible: "",
    poids: "",
    typeObjectif: "QUANTITATIF",
    priorite: "",
    typeIndicateur: "",
    surperformance: "",
    tolerance: "",
    dateFin: "",
};

function Field({ label, children, required }) {
    return (
        <div className="on-field">
            <label className="on-field__label">
                {label}
                {required && <span className="on-field__required"> *</span>}
            </label>
            {children}
        </div>
    );
}

function StatBadge({ value, label, color = "blue" }) {
    return (
        <div className={`on-stat on-stat--${color}`}>
            <span className="on-stat__value">{value}</span>
            <span className="on-stat__label">{label}</span>
        </div>
    );
}

const isWeightCounted = (objectif) => ["APPROVED", "PENDING_APPROVAL"].includes(objectif.approvalStatus);
const isApproved = (objectif) => objectif.approvalStatus === "APPROVED";
const isExpired = (dateFin) => {
    if (!dateFin) return false;
    const deadline = new Date(dateFin);
    deadline.setHours(23, 59, 59, 999);
    return new Date() > deadline;
};
const cleanPayload = (payload) => Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== "")
);
const revisionFields = [
    ["performanceCible", "Performance cible", "oldPerformanceCible"],
    ["poids", "Poids", "oldPoids"],
    ["dateFin", "Date d'echeance", "oldDateFin"],
    ["surperformance", "Surperformance", "oldSurperformance"],
    ["tolerance", "Tolerance", "oldTolerance"],
];
const modificationFields = [
    ["titre", "Titre", "oldTitre"],
    ["indicateur", "Indicateur", "oldIndicateur"],
    ["typeIndicateur", "Type indicateur", "oldTypeIndicateur"],
];
const objectifStatusLabel = (status) => {
    if (status === "APPROVED") return "Approuvé";
    if (status === "PENDING_APPROVAL") return "En attente d’approbation";
    if (status === "REJECTED") return "Rejeté";
    if (status === "REFUSED") return "Refusé";
    if (status === "EN_COURS") return "En cours";
    if (status === "TERMINE") return "Terminé";
    if (status === "COMPLETED") return "Terminé";
    if (status === "NOT_STARTED") return "Non démarré";
    if (status === "IN_PROGRESS") return "En cours";
    if (status === "PENDING_VALIDATION") return "En attente de validation";
    if (status === "BROUILLON") return "Brouillon";
    return status || "Objectif";
};

const requestStatusLabel = (status) => {
    if (status === "SOUMISE") return "En attente";
    if (status === "APPROUVEE") return "Approuvée";
    if (status === "REFUSEE") return "Refusée";
    if (status === "ANNULEE") return "Annulée";
    return status || "Demande";
};
const fieldsForRequest = (request) => request.requestType === "REVISION" ? revisionFields : modificationFields;
const showRequiredPopup = (text) => {
    Swal.fire({
        icon: "warning",
        title: "Champ obligatoire",
        text,
        confirmButtonText: "Compris",
    });
};

export default function ObjectifsNStep({ progressId }) {
    const dispatch = useDispatch();
    const slice = useSelector((state) => state.performance.objectifsN);
    const data = slice.dataByProgressId[String(progressId)];
    const loading = slice.loadingByProgressId[String(progressId)];
    const error = slice.errorByProgressId[String(progressId)];
    const objectifs = asArray(data);
    const stepper = useSelector((state) => state.performance.progress.stepperByProgressId[String(progressId)]);
    const modificationHistory = useSelector((state) => state.performance.modificationsObjectifs.byObjectifId);
    const [form, setForm] = useState(emptyObjectif);
    const [requestForms, setRequestForms] = useState({});
    const [formOpen, setFormOpen] = useState(true);
    const [aiModalOpen, setAiModalOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchObjectifsNRequest(progressId));
        dispatch(fetchProgressObjectifModificationRequestsRequest(progressId));
    }, [dispatch, progressId]);

    const updateForm = (patch) => setForm((current) => ({ ...current, ...patch }));

    const create = (event) => {
        event.preventDefault();
        if (form.poids === "" || form.poids == null) {
            showRequiredPopup("Poids nul, veuillez le saisir.");
            return;
        }
        dispatch(
            createObjectifRequest({
                ...form,
                evaluationProgressId: Number(progressId),
            })
        );
        setForm(emptyObjectif);
    };

    const updateRequestForm = (objectifId, requestType, patch) => {
        const key = `${objectifId}-${requestType}`;
        setRequestForms((current) => ({ ...current, [key]: { ...(current[key] || {}), ...patch } }));
    };

    const getRequestForm = (objectifId, requestType) => requestForms[`${objectifId}-${requestType}`] || {};

    const requestChange = (objectif, requestType) => {
        const values = getRequestForm(objectif.id, requestType);
        if (!String(values.justification || "").trim()) {
            showRequiredPopup("Veuillez remplir la justification avant d'envoyer la demande.");
            return;
        }
        const payload = requestType === "MODIFICATION"
            ? {
                requestType,
                titre: values.titre,
                indicateur: values.indicateur,
                typeIndicateur: values.typeIndicateur,
                justification: values.justification,
            }
            : {
                requestType,
                performanceCible: values.performanceCible,
                poids: values.poids,
                dateFin: values.dateFin,
                surperformance: values.surperformance,
                tolerance: values.tolerance,
                justification: values.justification,
            };
        dispatch(
            submitObjectifModificationRequest(
                objectif.id,
                cleanPayload(payload),
                progressId
            )
        );
    };

    const renderRequestHistory = (objectif) => {
        const history = asArray(modificationHistory[String(objectif.id)]);
        if (!history.length) return null;
        return (
            <div className="on-request-history">
                <div className="on-request-history__title">Historique des demandes</div>
                <div className="on-request-history__list">
                    {history.map((request) => (
                        <div className="on-request-history__item" key={request.id}>
                            <div className="on-request-history__head">
                                <span className="on-request-history__type">{request.requestType || "MODIFICATION"}</span>
                                <span className={`on-status-badge on-status-badge--${String(request.status || "").toLowerCase()}`}>
                                    {requestStatusLabel(request.status)}
                                </span>
                            </div>
                            <div className="on-request-history__meta">
                                {request.requestedAt ? new Date(request.requestedAt).toLocaleString("fr-FR") : "-"}
                                {request.decidedAt ? ` - Décision: ${new Date(request.decidedAt).toLocaleString("fr-FR")}` : ""}
                            </div>
                            {request.justification && <p className="on-request-history__text">Justification: {request.justification}</p>}
                            {request.decisionComment && <p className="on-request-history__text">Commentaire manager: {request.decisionComment}</p>}
                            <div className="on-request-history__diffs">
                                {fieldsForRequest(request).map(([field, label, oldField]) => request[field] != null && (
                                    <div className="on-request-history__diff" key={field}>
                                        <span>{label}</span>
                                        <strong>{request[oldField] ?? "-"} -> {request[field]}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const totalPoids = objectifs
        .filter(isWeightCounted)
        .reduce((sum, o) => sum + (Number(o.poids) || 0), 0);
    const aiStats = useMemo(() => buildObjectifsAiStats(objectifs, stepper), [objectifs, stepper]);
    const showAiComingSoon = () => {
        Swal.fire({
            icon: "info",
            title: "Assistance IA",
            text: "La configuration est prête. Le branchement de génération IA peut être ajouté côté backend.",
            confirmButtonText: "Compris",
        });
    };

    return (
        <StepState loading={loading} error={error} empty={false}>
            <div className="on-page">

                {/* ── HEADER STATS ── */}
                <div className="on-header">
                    <div className="on-header__left">
                        <h2 className="on-header__title">Objectifs N</h2>
                        <p className="on-header__sub">Définissez et gérez les objectifs de la période</p>
                    </div>
                    <div className="on-header__stats">
                        <StatBadge value={objectifs.length} label="Objectifs" color="blue" />
                        <StatBadge value={`${totalPoids}%`} label="Poids total" color={totalPoids === 100 ? "green" : totalPoids > 100 ? "red" : "orange"} />
                    </div>
                </div>

                {/* ── FORM CARD ── */}
                <div className="on-card">
                    <button
                        type="button"
                        className="on-card__toggle"
                        onClick={() => setFormOpen((v) => !v)}
                        aria-expanded={formOpen}
                    >
                        <span className="on-card__toggle-icon">{formOpen ? "−" : "+"}</span>
                        <span className="on-card__toggle-label">Nouvel objectif N</span>
                    </button>

                    {formOpen && (
                        <form className="on-form" onSubmit={create}>
                            <div className="on-form__section-label">Informations générales</div>
                            <div className="on-form__row on-form__row--3">
                                <Field label="Titre" required>
                                    <input
                                        className="on-input"
                                        required
                                        value={form.titre}
                                        onChange={(e) => updateForm({ titre: e.target.value })}
                                        placeholder="Intitulé de l'objectif"
                                    />
                                </Field>
                                <Field label="Priorité">
                                    <input
                                        className="on-input"
                                        type="number"
                                        min="1"
                                        value={form.priorite}
                                        onChange={(e) => updateForm({ priorite: e.target.value })}
                                        placeholder="1"
                                    />
                                </Field>
                                <Field label="Date d'échéance">
                                    <input
                                        className="on-input"
                                        type="date"
                                        value={form.dateFin}
                                        onChange={(e) => updateForm({ dateFin: e.target.value })}
                                    />
                                </Field>
                            </div>

                            <div className="on-form__section-label">Indicateurs & Mesure</div>
                            <div className="on-form__row on-form__row--4">
                                <Field label="Indicateur">
                                    <input
                                        className="on-input"
                                        value={form.indicateur}
                                        onChange={(e) => updateForm({ indicateur: e.target.value })}
                                        placeholder="KPI ou métrique"
                                    />
                                </Field>
                                <Field label="Type d'indicateur">
                                    <select
                                        className="on-select"
                                        value={form.typeIndicateur}
                                        onChange={(e) => updateForm({ typeIndicateur: e.target.value })}
                                    >
                                        <option value="">Sélectionner</option>
                                        <option value="Pourcentage">Pourcentage</option>
                                        <option value="Nombre">Nombre</option>
                                        <option value="Taux">Taux</option>
                                    </select>
                                </Field>
                                <Field label="Performance cible">
                                    <input
                                        className="on-input"
                                        type="number"
                                        step="0.01"
                                        value={form.performanceCible}
                                        onChange={(e) => updateForm({ performanceCible: e.target.value })}
                                        placeholder="0.00"
                                    />
                                </Field>
                                <Field label="Poids (%)">
                                    <input
                                        className="on-input"
                                        type="number"
                                        value={form.poids}
                                        onChange={(e) => updateForm({ poids: e.target.value })}
                                        placeholder="0"
                                    />
                                </Field>
                            </div>

                            <div className="on-form__row on-form__row--2">
                                <Field label="Surperformance">
                                    <input
                                        className="on-input"
                                        type="number"
                                        step="0.01"
                                        value={form.surperformance}
                                        onChange={(e) => updateForm({ surperformance: e.target.value })}
                                        placeholder="0.00"
                                    />
                                </Field>
                                <Field label="Tolérance">
                                    <input
                                        className="on-input"
                                        type="number"
                                        step="0.01"
                                        value={form.tolerance}
                                        onChange={(e) => updateForm({ tolerance: e.target.value })}
                                        placeholder="0.00"
                                    />
                                </Field>
                            </div>

                            <div className="on-form__section-label">Type d'objectif</div>
                            <div className="on-radio-group">
                                <label className={`on-radio-option ${form.typeObjectif === "QUANTITATIF" ? "on-radio-option--active" : ""}`}>
                                    <input
                                        type="radio"
                                        name="typeObjectif"
                                        value="QUANTITATIF"
                                        checked={form.typeObjectif === "QUANTITATIF"}
                                        onChange={(e) => updateForm({ typeObjectif: e.target.value })}
                                    />
                                    <span className="on-radio-option__dot" />
                                    <span>Quantitatif</span>
                                </label>
                                <label className={`on-radio-option ${form.typeObjectif === "QUALITATIF" ? "on-radio-option--active" : ""}`}>
                                    <input
                                        type="radio"
                                        name="typeObjectif"
                                        value="QUALITATIF"
                                        checked={form.typeObjectif === "QUALITATIF"}
                                        onChange={(e) => updateForm({ typeObjectif: e.target.value })}
                                    />
                                    <span className="on-radio-option__dot" />
                                    <span>Qualitatif</span>
                                </label>
                            </div>

                            <Field label="Description">
                                <textarea
                                    className="on-textarea"
                                    rows={3}
                                    value={form.description}
                                    onChange={(e) => updateForm({ description: e.target.value })}
                                    placeholder="Décrivez l'objectif en détail..."
                                />
                            </Field>

                            <div className="on-form__footer">
                                <button className="on-btn on-btn--primary" type="submit">
                                    <span>+</span> Ajouter l'objectif
                                </button>
                                <button className="on-btn on-btn--ai" type="button" onClick={() => setAiModalOpen(true)}>
                                    Assistance IA
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                <ObjectifsAIAssistanceModal
                    open={aiModalOpen}
                    onClose={() => setAiModalOpen(false)}
                    stats={aiStats}
                    onGenerate={showAiComingSoon}
                />

                {/* ── LIST ── */}
                <div className="on-list-header">
                    <h3 className="on-list-title">Objectifs enregistrés ({objectifs.length})</h3>
                </div>

                {objectifs.length === 0 ? (
                    <div className="on-empty">
                        <div className="on-empty__icon">◎</div>
                        <p>Aucun objectif N pour le moment.</p>
                        <span>Ajoutez votre premier objectif via le formulaire ci-dessus.</span>
                    </div>
                ) : (
                    <div className="on-stack">
                        {objectifs.map((objectif) => {
                            const status = objectif.approvalStatus || objectif.statusObjectif || "BROUILLON";
                            const expired = isExpired(objectif.dateFin);
                            const canRequest = isApproved(objectif) && !expired;
                            const modificationForm = getRequestForm(objectif.id, "MODIFICATION");
                            const revisionForm = getRequestForm(objectif.id, "REVISION");
                            return (
                                <article className="on-item" key={objectif.id}>
                                    <div className="on-item__head">
                                        <div className="on-item__head-left">
                                            <span className="on-item__priority">#{objectif.priorite ?? "—"}</span>
                                            <h3 className="on-item__title">{objectif.titre}</h3>
                                        </div>
                                        <div className="on-item__head-right">
                                            {objectif.poids != null && (
                                                <span className="on-poids-badge">{objectif.poids}%</span>
                                            )}
                                            <span className={`on-status-badge on-status-badge--${String(status).toLowerCase()}`}>
                                                {objectifStatusLabel(status)}
                                            </span>
                                        </div>
                                    </div>

                                    {objectif.description && (
                                        <p className="on-item__desc">{objectif.description}</p>
                                    )}

                                    <div className="on-facts">
                                        <div className="on-fact">
                                            <span className="on-fact__key">Type</span>
                                            <span className="on-fact__val">{objectif.typeObjectif}</span>
                                        </div>
                                        <div className="on-fact">
                                            <span className="on-fact__key">Indicateur</span>
                                            <span className="on-fact__val">{objectif.indicateur || "—"}</span>
                                        </div>
                                        <div className="on-fact">
                                            <span className="on-fact__key">Type ind.</span>
                                            <span className="on-fact__val">{objectif.typeIndicateur || "—"}</span>
                                        </div>
                                        <div className="on-fact">
                                            <span className="on-fact__key">Performance cible</span>
                                            <span className="on-fact__val on-fact__val--accent">{objectif.performanceCible ?? "—"}</span>
                                        </div>
                                        <div className="on-fact">
                                            <span className="on-fact__key">Surperf.</span>
                                            <span className="on-fact__val">{objectif.surperformance ?? "—"}</span>
                                        </div>
                                        <div className="on-fact">
                                            <span className="on-fact__key">Tolérance</span>
                                            <span className="on-fact__val">{objectif.tolerance ?? "—"}</span>
                                        </div>
                                        <div className="on-fact">
                                            <span className="on-fact__key">Échéance</span>
                                            <span className="on-fact__val">
                                                {objectif.dateFin ? new Date(objectif.dateFin).toLocaleDateString("fr-FR") : "—"}
                                            </span>
                                        </div>
                                    </div>

                                    {isApproved(objectif) && (
                                        <div className="on-item__footer">
                                            {/*<button className="on-btn on-btn--outline" type="button" disabled={!canRequest} title={expired ? "La date d'echeance est depassee." : ""} onClick={() => updateRequestForm(objectif.id, "MODIFICATION", { open: !modificationForm.open })}>*/}
                                            {/*    Demander modification Objectif N*/}
                                            {/*</button>*/}
                                            <button className="on-btn on-btn--outline" type="button" disabled={!canRequest} title={expired ? "La date d'echeance est depassee." : ""} onClick={() => updateRequestForm(objectif.id, "REVISION", { open: !revisionForm.open })}>
                                                Demander révision Objectif N
                                            </button>
                                            {expired && <span className="perf-state perf-state--warning">La date d'échéance est dépassée.</span>}
                                        </div>
                                    )}

                                    {canRequest && modificationForm.open && (
                                        <div className="on-form on-request-form">
                                            <div className="on-form__section-label">Modification</div>
                                            <div className="on-form__row on-form__row--3">
                                                <Field label="Titre"><input className="on-input" value={modificationForm.titre ?? objectif.titre ?? ""} onChange={(event) => updateRequestForm(objectif.id, "MODIFICATION", { titre: event.target.value })} /></Field>
                                                <Field label="Indicateur"><input className="on-input" value={modificationForm.indicateur ?? objectif.indicateur ?? ""} onChange={(event) => updateRequestForm(objectif.id, "MODIFICATION", { indicateur: event.target.value })} /></Field>
                                                <Field label="Type d'indicateur"><input className="on-input" value={modificationForm.typeIndicateur ?? objectif.typeIndicateur ?? ""} onChange={(event) => updateRequestForm(objectif.id, "MODIFICATION", { typeIndicateur: event.target.value })} /></Field>
                                            </div>
                                            <Field label="Justification" required><input className="on-input" value={modificationForm.justification || ""} onChange={(event) => updateRequestForm(objectif.id, "MODIFICATION", { justification: event.target.value })} /></Field>
                                            <button className="on-btn on-btn--primary" type="button" onClick={() => requestChange(objectif, "MODIFICATION")}>Envoyer la modification</button>
                                        </div>
                                    )}

                                    {canRequest && revisionForm.open && (
                                        <div className="on-form on-request-form">
                                            <div className="on-form__section-label">Révision</div>
                                            <div className="on-form__row on-form__row--3">
                                                <Field label="Performance cible"><input className="on-input" type="number" step="0.01" value={revisionForm.performanceCible ?? objectif.performanceCible ?? ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { performanceCible: event.target.value })} /></Field>
                                                <Field label="Poids"><input className="on-input" type="number" step="0.01" value={revisionForm.poids ?? objectif.poids ?? ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { poids: event.target.value })} /></Field>
                                                <Field label="Date d'echeance"><input className="on-input" type="date" value={revisionForm.dateFin ?? objectif.dateFin ?? ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { dateFin: event.target.value })} /></Field>
                                            </div>
                                            <div className="on-form__row on-form__row--2">
                                                <Field label="Surperformance"><input className="on-input" type="number" step="0.01" value={revisionForm.surperformance ?? objectif.surperformance ?? ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { surperformance: event.target.value })} /></Field>
                                                <Field label="Tolerance"><input className="on-input" type="number" step="0.01" value={revisionForm.tolerance ?? objectif.tolerance ?? ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { tolerance: event.target.value })} /></Field>
                                            </div>
                                            <Field label="Justification" required><input className="on-input" value={revisionForm.justification || ""} onChange={(event) => updateRequestForm(objectif.id, "REVISION", { justification: event.target.value })} /></Field>
                                            <button className="on-btn on-btn--accent on-btn--compact" type="button" onClick={() => requestChange(objectif, "REVISION")}>Envoyer la révision</button>
                                        </div>
                                    )}
                                    {renderRequestHistory(objectif)}
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </StepState>
    );
}
